import React, { useCallback, useRef } from "react";
import { Grid, Card, CardContent, Typography, Box } from "@mui/material";
import { motion, useInView } from "framer-motion";

import bloodImg from "../assets/blood.webp";
import menuImg from "../assets/menu.webp";
import accountingImg from "../assets/accounting.webp";

const Projects = () => {
  const ref = useRef(null);

  // 👇 detect scroll
  const isInView = useInView(ref, {
    amount: 0.3,
    once: false,
  });

  const projects = [
    {
      title: "Blood Donation System",
      desc: "Full-stack system for managing donors, requests, and blood matching.",
      image: bloodImg,
      link: "https://69d51d741614b1000834de71--redlink7.netlify.app",
    },
    {
      title: "Restaurant Menu Website",
      desc: "Responsive menu website with categories and smooth UI experience.",
      image: menuImg,
      link: "https://qr-menu22.vercel.app",
    },
    {
      title: "Accounting System (ERP)",
      desc: "Accounting system with VAT, payroll, invoices, and reports.",
      image: accountingImg,
      link: null,
    },
  ];

  const openLink = useCallback((link) => {
    if (link) window.open(link, "_blank", "noopener,noreferrer");
  }, []);

  return (
    <Box
      ref={ref}
      id="projects"
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 2, sm: 3, md: 4 },

        // 🎯 background change on scroll
        background: isInView
          ? "linear-gradient(135deg, #000000, #1a1a1a)"
          : "#ffffff",

        color: isInView ? "#ffffff" : "#000000",

        transition: "all 0.6s ease",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <Box maxWidth="1100px" mx="auto">

        {/* TITLE */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              textAlign: "center",
              mb: 1,
            }}
          >
            My Projects
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              mb: 6,
              color: isInView ? "#bbb" : "gray",
            }}
          >
            ERP • Accounting • Web Systems
          </Typography>
        </motion.div>

        {/* GRID */}
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {projects.map((project, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={index}
              sx={{ display: "flex" }}
            >
              <motion.div
                style={{ width: "100%" }}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <Card
                  onClick={() => openLink(project.link)}
                  sx={{
                    width: "100%",
                    height: "100%",
                    cursor: project.link ? "pointer" : "default",

                    borderRadius: 3,
                    overflow: "hidden",

                    backgroundColor: isInView ? "#1a1a1a" : "#fff",
                    color: isInView ? "#fff" : "#000",

                    boxShadow: isInView
                      ? "0 12px 30px rgba(0,0,0,0.6)"
                      : "0 6px 20px rgba(0,0,0,0.08)",

                    transition: "0.3s",

                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: isInView
                        ? "0 20px 45px rgba(0,0,0,0.8)"
                        : "0 12px 30px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  {/* IMAGE */}
                  <Box
                    sx={{
                      width: "100%",
                      height: 180,
                      overflow: "hidden",
                      backgroundColor: isInView ? "#111" : "#f5f5f5",
                    }}
                  >
                    <Box
                      component="img"
                      src={project.image}
                      alt={project.title}
                      loading="lazy"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "0.4s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                  </Box>

                  <CardContent>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>
                      {project.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 14,
                        color: isInView ? "#ccc" : "#555",
                      }}
                    >
                      {project.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

      </Box>
    </Box>
  );
};

export default Projects;